/**
 * FieldError — muestra un mensaje de error inline bajo un campo.
 */
export function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        marginTop: 4,
        color: "#f87171",
        fontSize: "0.78rem",
      }}
    >
      <i
        className="fa-solid fa-circle-exclamation"
        style={{ fontSize: "0.72rem" }}
      />
      {msg}
    </div>
  );
}

/**
 * CedulaStatus — muestra el resultado de la búsqueda de cédula.
 */
export function CedulaStatus({ estado }) {
  if (estado === "idle") return null;

  const cfg = {
    buscando: {
      color: "#60a5fa",
      icon: "fa-circle-notch fa-spin",
      text: "Buscando…",
    },
    local: {
      color: "#4ade80",
      icon: "fa-circle-check",
      text: "Datos cargados desde el registro local",
    },
    tse: {
      color: "#4ade80",
      icon: "fa-circle-check",
      text: "Datos cargados desde el TSE (Registro Nacional CR)",
    },
    no_encontrado: {
      color: "#fbbf24",
      icon: "fa-triangle-exclamation",
      text: "Cédula no encontrada. Ingresá el nombre manualmente.",
    },
    error: {
      color: "#f87171",
      icon: "fa-circle-exclamation",
      text: "No se pudo consultar el registro. Ingresá el nombre manualmente.",
    },
  }[estado];

  if (!cfg) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: 5,
        color: cfg.color,
        fontSize: "0.78rem",
      }}
    >
      <i className={`fa-solid ${cfg.icon}`} />
      {cfg.text}
    </div>
  );
}

/**
 * ForeignToggle — chip seleccionable "Es número extranjero" para teléfono/placa.
 * Se ve como una etiqueta tipo píldora: gris cuando está apagada, naranja al activarla.
 */
export function ForeignToggle({ checked, onChange, label, compact = false }) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        marginTop: compact ? 0 : 7,
        whiteSpace: "nowrap",
        padding: "3px 10px",
        borderRadius: 999,
        border: `1px solid ${checked ? "rgba(249,115,22,0.55)" : "var(--border-card, rgba(148,163,184,0.3))"}`,
        background: checked ? "rgba(249,115,22,0.12)" : "transparent",
        fontSize: "0.74rem",
        fontWeight: 600,
        color: checked ? "#fb923c" : "var(--text-muted)",
        userSelect: "none",
        cursor: "pointer",
        transition: "all 0.15s",
        width: "fit-content",
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        style={{ display: "none" }}
      />
      <i
        className={`fa-solid ${checked ? "fa-circle-check" : "fa-globe"}`}
        style={{ fontSize: "0.7rem" }}
      />
      {label}
    </label>
  );
}

/**
 * TipoPersonaSwitch — elige si la persona es física o una sociedad.
 * value: true = sociedad. Cambia qué campos de nombre se muestran.
 */
export function TipoPersonaSwitch({ value, onChange }) {
  const opcion = (esSociedad, icon, label) => {
    const activo = !!value === esSociedad;
    return (
      <button
        type="button"
        onClick={() => onChange(esSociedad)}
        aria-pressed={activo}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
          padding: "6px 12px",
          border: "none",
          borderRadius: 6,
          fontSize: "0.82rem",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.15s",
          background: activo ? "#f97316" : "transparent",
          color: activo ? "#fff" : "var(--text-muted)",
        }}
      >
        <i className={`fa-solid ${icon}`} style={{ fontSize: "0.78rem", color: "inherit" }} />
        {label}
      </button>
    );
  };
  return (
    <div
      role="group"
      style={{
        display: "flex",
        gap: 4,
        padding: 3,
        borderRadius: 8,
        background: "var(--bg-input)",
        border: "1px solid var(--border-card, rgba(148,163,184,0.2))",
      }}
    >
      {opcion(false, "fa-user", "Persona física")}
      {opcion(true, "fa-building", "Sociedad")}
    </div>
  );
}

/**
 * VehiculosField — lista editable de placas (una persona puede tener varias).
 * vehiculos: [{ placa, extranjera }]. errors: array de mensajes por placa.
 */
export function VehiculosField({ vehiculos = [], onChange, errors, controlClass }) {
  function cambiar(idx, campo, valor) {
    onChange(vehiculos.map((v, i) => (i === idx ? { ...v, [campo]: valor } : v)));
  }
  function quitar(idx) {
    onChange(vehiculos.filter((_, i) => i !== idx));
  }
  function agregar() {
    onChange([...vehiculos, { placa: "", extranjera: false }]);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {vehiculos.map((v, idx) => (
        <div key={idx}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="text"
              className={controlClass}
              style={{ flex: 1, borderColor: errors?.[idx] ? "#ef4444" : undefined }}
              placeholder={v.extranjera ? "Placa extranjera" : "Ej: ABC-123"}
              value={v.placa}
              onChange={(e) => cambiar(idx, "placa", e.target.value.toUpperCase())}
            />
            <ForeignToggle
              checked={v.extranjera}
              onChange={() => cambiar(idx, "extranjera", !v.extranjera)}
              label="Extranjera"
              compact
            />
            <button
              type="button"
              onClick={() => quitar(idx)}
              title="Quitar placa"
              style={{
                padding: "6px 10px",
                background: "rgba(248,113,113,0.1)",
                border: "1px solid rgba(248,113,113,0.3)",
                borderRadius: 6,
                color: "#f87171",
                cursor: "pointer",
              }}
            >
              <i className="fa-solid fa-trash" style={{ color: "inherit", fontSize: "0.8rem" }} />
            </button>
          </div>
          <FieldError msg={errors?.[idx]} />
        </div>
      ))}
      <button
        type="button"
        onClick={agregar}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 12px",
          borderRadius: 999,
          border: "1px dashed rgba(249,115,22,0.55)",
          background: "transparent",
          color: "#fb923c",
          fontSize: "0.78rem",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <i className="fa-solid fa-plus" style={{ color: "inherit", fontSize: "0.72rem" }} />
        {vehiculos.length ? "Agregar otra placa" : "Agregar placa"}
      </button>
    </div>
  );
}

/**
 * SectionLabel — separador de sección dentro de un step del wizard.
 * Texto pequeño en mayúsculas con línea divisoria a la derecha.
 */
export function SectionLabel({ icon, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginTop: 6,
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.8px",
          textTransform: "uppercase",
          color: "#f97316",
          whiteSpace: "nowrap",
        }}
      >
        {icon && <i className={`fa-solid ${icon}`} style={{ fontSize: "0.72rem" }} />}
        {children}
      </span>
      <span style={{ flex: 1, height: 1, background: "var(--border-card, rgba(148,163,184,0.18))" }} />
    </div>
  );
}
